import React, { useContext, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { AuthContext } from '../contexts/AuthContext';
import AuthLayout from '../components/AuthLayout';
import { Input, PasswordInput } from '../components/forms';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

const Login: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const authContext = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get return URL and any messages from location state
  const returnTo = location.state?.returnTo;
  const stateMessage = location.state?.message;
  const prefilledEmail = location.state?.email;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    defaultValues: {
      email: prefilledEmail || '',
      password: '',
      rememberMe: false,
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await authContext?.login(data.email, data.password);
      if (result?.success) {
        // Redirect to returnTo URL if available, otherwise dashboard
        navigate(returnTo || '/dashboard');
      } else {
        setError(result?.message || t('auth.loginError'));
      }
    } catch {
      setError(t('auth.anErrorOccurred'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout title={t('auth.signIn')} subtitle={t('auth.signInToManageAccount')}>
      {stateMessage && (
        <div className="mb-6 p-4 bg-brand-50 border border-brand-200 rounded-lg">
          <p className="text-sm text-brand-primary">{stateMessage}</p>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Email field */}
        <div className="space-y-1">
          <label className="text-xs font-normal text-[#1F1F1F] leading-[1.6666666666666667em]">
            {t('auth.email')}
          </label>
          <Input
            {...register('email', {
              required: t('auth.errors.emailRequired'),
              pattern: {
                value: /^\S+@\S+$/i,
                message: t('auth.errors.validEmailRequired'),
              },
            })}
            type="email"
            placeholder={t('auth.enterYourEmail')}
            error={errors.email?.message}
            className="w-full border border-[#DEDEE4] rounded-md px-4 py-[14px] text-base font-normal text-[#6E7077] leading-[1.75em] focus:border-[var(--brand-primary)] focus:ring-[var(--brand-primary)]"
          />
        </div>

        {/* Password field */}
        <div className="space-y-1">
          <label className="text-xs font-normal text-[#1F1F1F] leading-[1.6666666666666667em]">
            {t('auth.password')}
          </label>
          <PasswordInput
            {...register('password', {
              required: t('auth.errors.passwordRequired'),
              minLength: {
                value: 6,
                message: t('auth.errors.passwordMinLength', { length: 6 }),
              },
            })}
            placeholder="**************"
            error={errors.password?.message}
            className="w-full border border-[#DEDEE4] rounded-md px-4 py-[14px] text-base font-normal text-[#6E7077] leading-[1.75em] focus:border-[var(--brand-primary)] focus:ring-[var(--brand-primary)]"
          />
        </div>

        {/* Remember me and forgot password */}
        <div className="flex items-center justify-between py-2">
          <div className="flex items-center gap-2">
            <input
              {...register('rememberMe')}
              type="checkbox"
              className="w-5 h-5 text-[var(--brand-primary)] border-[#DEDEE4] rounded focus:ring-[var(--brand-primary)] checked:bg-[var(--brand-primary)] checked:border-[var(--brand-primary)]"
            />
            <label className="text-base font-normal text-[#1F1F1F] leading-[1.75em]">
              {t('auth.rememberMe')}
            </label>
          </div>
          <Link
            to="/forgot-password"
            className="text-base font-normal text-[#1F1F1F] leading-[1.75em] hover:text-[var(--brand-primary)] transition-colors"
          >
            {t('auth.forgotPassword')}
          </Link>
        </div>

        {/* Sign in button */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white font-bold py-[14px] px-[30px] rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm leading-[1.2102272851126534em] uppercase tracking-wide"
        >
          {isLoading ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              <span>{t('auth.signingIn')}</span>
            </div>
          ) : (
            t('auth.signIn').toUpperCase()
          )}
        </button>

        {/* Sign up link */}
        <div className="text-right pt-2">
          <p className="text-base font-normal text-[#1F1F1F] leading-[1.75em]">
            {t('auth.dontHaveAccount')}{' '}
            <Link
              to="/register"
              className="text-[var(--brand-primary)] hover:text-[var(--brand-primary-hover)] font-medium transition-colors"
            >
              {t('auth.signUp')}
            </Link>
          </p>
        </div>
      </form>
    </AuthLayout>
  );
};

export default Login;
